import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/sidebar/Sidebar';
import songAdService, {
  MotionMode,
  SongAdProject,
  SongAdTimeline,
  TelopPosition,
  TelopSize,
  UpdateSongAdProjectParams,
  UpdateSongAdSceneInput,
} from '../../services/api/songAdService';
import InfoStep from '../../components/song-ad/InfoStep';
import LyricsStep from '../../components/song-ad/LyricsStep';
import SongStep from '../../components/song-ad/SongStep';
import StoryboardStep from '../../components/song-ad/StoryboardStep';
import MotionStep from '../../components/song-ad/MotionStep';
import PreviewStep from '../../components/song-ad/PreviewStep';
import EditorStep from '../../components/song-ad/EditorStep';
import ExportStep from '../../components/song-ad/ExportStep';
import { SongAdSceneDraft } from '../../components/song-ad/types';
import { ResponsiveAppShell, TaskyBadge, TaskyAlert, TaskyTopBar, TaskyPageContainer } from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import {
  GENERATION_BUSY_MESSAGE,
  isTooManyJobsError,
  useGenerationCooldown,
} from '../../hooks/useGenerationCooldown';
import {
  Music2,
  ArrowLeft,
  Loader2,
  ClipboardList,
  FileText,
  ImageIcon,
  Wand2,
  Combine,
  CheckCircle2,
  PlayCircle,
} from 'lucide-react';

/**
 * 歌広告（Song Ad Video）エディタ。
 *
 * ①情報登録 → ②歌詞 → ③楽曲 → ④ストーリーボード → ⑤モーション → ⑥プレビュー → ⑦書き出し の
 * 7ステップをタブで自由に行き来できる（MovieProjectEditorPage 踏襲。ウィザード式の強制遷移はしない）。
 * v5 e: 「作成設定を変更」モーダルは廃止し、①情報登録タブ（`InfoStep`）に統合した。
 *
 * 「詳細編集」（'editor'）だけはタブに出さない特殊ステップ（spec §14。mockup.htmlの screen-editor も
 * STEPS ナビに含まれない）。⑥プレビューの「詳細編集する」ボタンからのみ入り、自身のツールバーの
 * 「戻る/書き出す」で ⑥/⑦ に戻る。
 *
 * - サーバー状態（project）はポーリングで更新される「真実」
 * - 歌詞テキスト・シーン設定（モーション・テロップ位置/サイズ・歌詞行）のローカルドラフトは
 *   保存操作のときだけサーバーへ送り、ポーリングでは上書きしない
 * - 並び替え・追加・削除は構造変更のため、ドラフトに関わらず即PUT scenesする
 * - ①情報登録タブの保存内容（作成設定・商品・画像・タイトル）は `InfoStep` 側でローカルドラフトを持ち、
 *   保存時にのみ PATCH /song-ads/{id} で送る（v5 e。旧: 作成設定を再利用したモーダル）
 */

type StepKey = 'info' | 'lyrics' | 'song' | 'storyboard' | 'motion' | 'preview' | 'editor' | 'export';

const POLLING_INTERVAL_MS = 5000;

const toSceneDrafts = (project: SongAdProject): SongAdSceneDraft[] =>
  (project.scenes ?? []).map((scene) => ({
    id: scene.id,
    lyricLinesText: scene.lyric_lines.join('\n'),
    motionMode: scene.motion_mode,
    motionPrompt: scene.motion_prompt ?? '',
    telopPosition: scene.telop_position,
    telopSize: scene.telop_size,
  }));

const hasActiveWork = (project: SongAdProject): boolean =>
  project.status === 'song_generating' ||
  project.status === 'composing' ||
  (project.scenes ?? []).some(
    (s) =>
      s.storyboard.status === 'pending' ||
      s.storyboard.status === 'processing' ||
      s.clip.status === 'pending' ||
      s.clip.status === 'processing'
  );

const SongAdEditorPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // 既定タブは①情報登録（v6 F1・m）。7タブ化後も既定が②歌詞のままだったため、作成直後に遷移すると
  // ①情報登録の内容（商品・画像・設定）が見えず、タブを手動で押して初めて気づく状態になっていた。
  const [activeStep, setActiveStep] = useState<StepKey>('info');
  const [project, setProject] = useState<SongAdProject | null>(null);
  const [lyricsDraft, setLyricsDraft] = useState('');
  const [kanaDraft, setKanaDraft] = useState('');
  const [sceneDrafts, setSceneDrafts] = useState<SongAdSceneDraft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isGeneratingLyrics, setIsGeneratingLyrics] = useState(false);
  const [isSavingLyrics, setIsSavingLyrics] = useState(false);
  const [isRegeneratingKana, setIsRegeneratingKana] = useState(false);
  const [isGeneratingSong, setIsGeneratingSong] = useState(false);
  const [isFetchingTimings, setIsFetchingTimings] = useState(false);
  const [isGeneratingScenes, setIsGeneratingScenes] = useState(false);
  const [isSavingSceneDrafts, setIsSavingSceneDrafts] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [isMotionAutoRequesting, setIsMotionAutoRequesting] = useState(false);
  const [isComposeRequesting, setIsComposeRequesting] = useState(false);
  const [generatingSceneIds, setGeneratingSceneIds] = useState<Set<string>>(new Set());
  // 絵コンテ生成の連打対策。state は再描画まで古いので、再入の判定は ref で行う。
  const storyboardInFlightRef = useRef<Set<string>>(new Set());
  const bulkStoryboardRunningRef = useRef(false);
  const [isGeneratingAllStoryboards, setIsGeneratingAllStoryboards] = useState(false);
  const {
    coolingDown: storyboardCoolingDown,
    start: startStoryboardCooldown,
    isActive: isStoryboardCoolingDown,
  } = useGenerationCooldown();

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const applyData = useCallback((next: SongAdProject, resetDrafts: boolean) => {
    setProject(next);
    if (resetDrafts) {
      setLyricsDraft(next.lyrics ?? '');
      setKanaDraft(next.lyrics_kana ?? '');
      setSceneDrafts(toSceneDrafts(next));
    }
  }, []);

  const fetchProject = useCallback(
    async (resetDrafts: boolean) => {
      if (!projectId) return;
      try {
        const next = await songAdService.getProject(projectId);
        applyData(next, resetDrafts);
        setError(null);
      } catch (e) {
        setError(songAdService.extractErrorMessage(e));
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, applyData]
  );

  useEffect(() => {
    fetchProject(true);
  }, [fetchProject]);

  // アクティブなジョブがある間だけポーリング（ドラフトは触らない）
  useEffect(() => {
    if (!project || !hasActiveWork(project)) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    if (pollingRef.current) return;

    pollingRef.current = setInterval(() => fetchProject(false), POLLING_INTERVAL_MS);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [project, fetchProject]);

  /** 402 credit_insufficient はトースト、それ以外はページ内エラー表示にする（グローバルリスナーは存在しない） */
  const handleActionError = useCallback(
    (e: unknown) => {
      if (songAdService.isCreditInsufficientError(e)) {
        showToast('クレジットが不足しています。プランをご確認ください。', 'error');
      } else {
        setError(songAdService.extractErrorMessage(e));
      }
    },
    [showToast]
  );

  const handleGenerateLyrics = useCallback(
    async (instruction?: string) => {
      if (!projectId) return;
      setIsGeneratingLyrics(true);
      setError(null);
      try {
        const next = await songAdService.generateLyrics(projectId, instruction);
        applyData(next, true);
        showToast(instruction ? 'AIで歌詞を修正しました' : '歌詞を生成しました');
      } catch (e) {
        handleActionError(e);
      } finally {
        setIsGeneratingLyrics(false);
      }
    },
    [projectId, applyData, showToast, handleActionError]
  );

  /**
   * 表示歌詞＋かな歌詞の両方をそのまま保存する（サーバー側の再生成なし。行数不一致は422）。
   * 保存に成功したら②楽曲ステップへ自動遷移する（f・g）。失敗時は遷移しない。
   */
  const handleSaveFixedLyrics = useCallback(async () => {
    if (!projectId || !lyricsDraft.trim() || !kanaDraft.trim()) return;
    setIsSavingLyrics(true);
    setError(null);
    try {
      const next = await songAdService.updateLyrics(projectId, lyricsDraft, kanaDraft);
      applyData(next, true);
      showToast('歌詞を保存しました');
      setActiveStep('song');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsSavingLyrics(false);
    }
  }, [projectId, lyricsDraft, kanaDraft, applyData, showToast, handleActionError]);

  /** 表示歌詞のみ送信し、サーバー側でかな歌詞を再生成する（「読みを自動で振り直す」） */
  const handleRegenerateKana = useCallback(async () => {
    if (!projectId || !lyricsDraft.trim()) return;
    setIsRegeneratingKana(true);
    setError(null);
    try {
      const next = await songAdService.updateLyrics(projectId, lyricsDraft);
      applyData(next, true);
      showToast('かな歌詞を振り直しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsRegeneratingKana(false);
    }
  }, [projectId, lyricsDraft, applyData, showToast, handleActionError]);

  const handleGenerateSong = useCallback(async () => {
    if (!projectId) return;
    setIsGeneratingSong(true);
    setError(null);
    try {
      const next = await songAdService.generateSong(projectId);
      applyData(next, false);
      showToast('曲の生成を開始しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsGeneratingSong(false);
    }
  }, [projectId, applyData, showToast, handleActionError]);

  const handleGenerateScenes = useCallback(async () => {
    if (!projectId) return;
    setIsGeneratingScenes(true);
    setError(null);
    try {
      const next = await songAdService.generateScenes(projectId);
      applyData(next, true);
      showToast('シーンに分割しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsGeneratingScenes(false);
    }
  }, [projectId, applyData, showToast, handleActionError]);

  const markGenerating = useCallback((sceneId: string, on: boolean) => {
    setGeneratingSceneIds((prev) => {
      const next = new Set(prev);
      if (on) {
        next.add(sceneId);
      } else {
        next.delete(sceneId);
      }
      return next;
    });
  }, []);

  const handleGenerateStoryboard = useCallback(
    async (sceneId: string, options?: { suppressToast?: boolean }): Promise<boolean> => {
      if (!projectId) return false;
      if (storyboardInFlightRef.current.has(sceneId)) return false;
      if (isStoryboardCoolingDown()) {
        setError(GENERATION_BUSY_MESSAGE);
        return false;
      }
      storyboardInFlightRef.current.add(sceneId);
      markGenerating(sceneId, true);
      setError(null);
      try {
        await songAdService.generateStoryboard(projectId, sceneId);
        await fetchProject(false);
        if (!options?.suppressToast) {
          showToast('絵コンテの生成を開始しました');
        }
        return true;
      } catch (e) {
        if (isTooManyJobsError(e)) {
          // 同時実行枠が空くまで同じ 429 が返るので、しばらく押せなくする
          startStoryboardCooldown();
          setError(`${GENERATION_BUSY_MESSAGE}（${songAdService.extractErrorMessage(e)}）`);
        } else {
          handleActionError(e);
        }
        return false;
      } finally {
        storyboardInFlightRef.current.delete(sceneId);
        markGenerating(sceneId, false);
      }
    },
    [projectId, fetchProject, markGenerating, showToast, handleActionError, isStoryboardCoolingDown, startStoryboardCooldown]
  );

  const handleGenerateClip = useCallback(
    async (sceneId: string, options?: { suppressToast?: boolean }): Promise<boolean> => {
      if (!projectId) return false;
      markGenerating(sceneId, true);
      setError(null);
      try {
        await songAdService.generateClip(projectId, sceneId);
        await fetchProject(false);
        if (!options?.suppressToast) {
          showToast('動画クリップの生成を開始しました');
        }
        return true;
      } catch (e) {
        handleActionError(e);
        return false;
      } finally {
        markGenerating(sceneId, false);
      }
    },
    [projectId, fetchProject, markGenerating, showToast, handleActionError]
  );

  const handleGenerateAllStoryboards = useCallback(
    async (options?: { auto?: boolean }) => {
      if (!project || bulkStoryboardRunningRef.current) return;
      const targets = (project.scenes ?? []).filter(
        (s) => s.storyboard.status === 'none' || s.storyboard.status === 'failed'
      );
      if (targets.length === 0) return;
      bulkStoryboardRunningRef.current = true;
      setIsGeneratingAllStoryboards(true);
      let toastShown = false;
      try {
        for (const scene of targets) {
          // eslint-disable-next-line no-await-in-loop
          const succeeded = await handleGenerateStoryboard(scene.id, { suppressToast: true });
          // 429 の後は残りのシーンも同じく 429 になるので打ち切る
          if (!succeeded && isStoryboardCoolingDown()) break;
          if (succeeded && !toastShown) {
            // v8: 自動実行の場合は「自動で」明記＋件数を出す（クレジットを消費する処理なので、
            // ユーザー操作なしで走ったことが分かるようにする）
            showToast(
              options?.auto
                ? `シーン表示に続けて絵コンテの生成を自動で開始しました（${targets.length}件・クレジットを消費します）`
                : '絵コンテの生成を開始しました'
            );
            toastShown = true;
          }
        }
      } finally {
        bulkStoryboardRunningRef.current = false;
        setIsGeneratingAllStoryboards(false);
      }
    },
    [project, handleGenerateStoryboard, showToast, isStoryboardCoolingDown]
  );

  const handleGenerateAllClips = useCallback(async () => {
    if (!project) return;
    const targets = (project.scenes ?? []).filter((s) => {
      const draft = sceneDrafts.find((d) => d.id === s.id);
      const mode = draft?.motionMode ?? s.motion_mode;
      return mode === 'clip' && s.storyboard.image && (s.clip.status === 'none' || s.clip.status === 'failed');
    });
    let toastShown = false;
    for (const scene of targets) {
      // eslint-disable-next-line no-await-in-loop
      const succeeded = await handleGenerateClip(scene.id, { suppressToast: true });
      if (succeeded && !toastShown) {
        showToast('動画クリップの生成を開始しました');
        toastShown = true;
      }
    }
  }, [project, sceneDrafts, handleGenerateClip, showToast]);

  const handleSceneLyricLinesChange = useCallback((sceneId: string, text: string) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, lyricLinesText: text } : d)));
  }, []);

  const handleSetMotionMode = useCallback((sceneId: string, motionMode: MotionMode) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, motionMode } : d)));
  }, []);

  const handleSetMotionModeAll = useCallback((motionMode: MotionMode) => {
    setSceneDrafts((prev) => prev.map((d) => ({ ...d, motionMode })));
  }, []);

  const handleMotionPromptChange = useCallback((sceneId: string, motionPrompt: string) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, motionPrompt } : d)));
  }, []);

  const handleTelopPositionChange = useCallback((sceneId: string, telopPosition: TelopPosition) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, telopPosition } : d)));
  }, []);

  const handleTelopSizeChange = useCallback((sceneId: string, telopSize: TelopSize) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, telopSize } : d)));
  }, []);

  const draftsToScenePayload = useCallback(
    (drafts: SongAdSceneDraft[]): UpdateSongAdSceneInput[] =>
      drafts.map((draft) => ({
        id: draft.id,
        motion_mode: draft.motionMode,
        motion_prompt: draft.motionPrompt.trim() || null,
        lyric_lines: draft.lyricLinesText
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
        telop_position: draft.telopPosition,
        telop_size: draft.telopSize,
      })),
    []
  );

  const handleSaveSceneDrafts = useCallback(async () => {
    if (!projectId) return;
    setIsSavingSceneDrafts(true);
    setError(null);
    try {
      const next = await songAdService.updateScenes(projectId, draftsToScenePayload(sceneDrafts));
      applyData(next, true);
      showToast('設定を保存しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsSavingSceneDrafts(false);
    }
  }, [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]);

  /**
   * v8: ⑤モーションの「保存して次へ」。保存に成功したら⑥プレビューへ自動遷移する
   * （①情報登録・②歌詞の「保存して次へ」＝handleSaveInfo/handleSaveFixedLyrics と同じパターン。
   * 失敗時（handleActionError側）は遷移しない）。
   * ④ストーリーボードの「設定を保存」（handleSaveSceneDrafts）はこの遷移をしない別ボタンのまま。
   */
  const handleSaveMotionAndProceed = useCallback(async () => {
    if (!projectId) return;
    setIsSavingSceneDrafts(true);
    setError(null);
    try {
      const next = await songAdService.updateScenes(projectId, draftsToScenePayload(sceneDrafts));
      applyData(next, true);
      showToast('設定を保存しました');
      setActiveStep('preview');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsSavingSceneDrafts(false);
    }
  }, [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]);

  /** シーンの並び替え。ドラッグ&ドロップで確定した新しいid順を即PUTする（ドラフト保存を待たない） */
  const handleReorderScenes = useCallback(
    async (orderedSceneIds: string[]) => {
      if (!projectId) return;
      const bySceneId = new Map(sceneDrafts.map((d) => [d.id, d]));
      const reorderedDrafts = orderedSceneIds
        .map((id) => bySceneId.get(id))
        .filter((d): d is SongAdSceneDraft => !!d);
      setSceneDrafts(reorderedDrafts);
      setIsReordering(true);
      setError(null);
      try {
        const next = await songAdService.updateScenes(projectId, draftsToScenePayload(reorderedDrafts));
        applyData(next, true);
        showToast('シーンを並び替えました');
      } catch (e) {
        handleActionError(e);
        await fetchProject(false);
      } finally {
        setIsReordering(false);
      }
    },
    [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError, fetchProject]
  );

  /** シーン追加。id無し要素をPUT scenesに含めると新規シーンとして作成される（B4仕様） */
  const handleAddScene = useCallback(async () => {
    if (!projectId) return;
    setIsReordering(true);
    setError(null);
    try {
      const payload: UpdateSongAdSceneInput[] = [
        ...draftsToScenePayload(sceneDrafts),
        // v6 F3/r: 既定サイズを 中(md) → 大(lg) に変更（9:16で 46px→56px、約+20%）
        { motion_mode: 'still', motion_prompt: null, lyric_lines: [], telop_position: 'bottom', telop_size: 'lg' },
      ];
      const next = await songAdService.updateScenes(projectId, payload);
      applyData(next, true);
      showToast('シーンを追加しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsReordering(false);
    }
  }, [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]);

  /** シーン削除。送らなかった既存idはPUT scenesで削除される（B4仕様） */
  const handleDeleteScene = useCallback(
    async (sceneId: string) => {
      if (!projectId) return;
      const remaining = sceneDrafts.filter((d) => d.id !== sceneId);
      setIsReordering(true);
      setError(null);
      try {
        const next = await songAdService.updateScenes(projectId, draftsToScenePayload(remaining));
        applyData(next, true);
        showToast('シーンを削除しました');
      } catch (e) {
        handleActionError(e);
      } finally {
        setIsReordering(false);
      }
    },
    [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]
  );

  /** シーン単位AI修正（202・非同期job）。完了はポーリングで反映されるため即fetchして進行中表示に切り替える */
  const handleAiEditScene = useCallback(
    async (sceneId: string, instruction: string) => {
      if (!projectId) return;
      markGenerating(sceneId, true);
      setError(null);
      try {
        await songAdService.aiEditScene(projectId, sceneId, instruction);
        await fetchProject(false);
        showToast('AI修正を開始しました');
      } catch (e) {
        handleActionError(e);
      } finally {
        markGenerating(sceneId, false);
      }
    },
    [projectId, fetchProject, markGenerating, showToast, handleActionError]
  );

  const handleReplaceSceneImage = useCallback(
    async (sceneId: string, s3Key: string) => {
      if (!projectId) return;
      markGenerating(sceneId, true);
      setError(null);
      try {
        const next = await songAdService.replaceSceneImage(projectId, sceneId, s3Key);
        applyData(next, true);
        showToast('画像を差し替えました');
      } catch (e) {
        handleActionError(e);
      } finally {
        markGenerating(sceneId, false);
      }
    },
    [projectId, applyData, markGenerating, showToast, handleActionError]
  );

  const handleMotionAuto = useCallback(async () => {
    if (!projectId) return;
    setIsMotionAutoRequesting(true);
    setError(null);
    try {
      const next = await songAdService.motionAuto(projectId);
      applyData(next, true);
      showToast('サビ・商品・CTAのシーンを優先して動画化に設定しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsMotionAutoRequesting(false);
    }
  }, [projectId, applyData, showToast, handleActionError]);

  /**
   * 歌詞行ごとのタイミング取得（v4 F8/B1）。POST /song-ads/{id}/timings はscenesに依存しないため
   * シーン未生成でも呼べる。シーンが既にあり、まだ詳細編集用テロップ（timeline.captions）が
   * 無ければ合わせて初期化する（captions/init は依然シーン必須。無ければ422になるが
   * タイミング取得自体は成功しているので黙って無視し、ブロックしない）。
   */
  const handleFetchTimings = useCallback(async () => {
    if (!projectId) return;
    setIsFetchingTimings(true);
    setError(null);
    try {
      let next = await songAdService.fetchTimings(projectId);
      if (!next.timeline?.captions?.length) {
        try {
          next = await songAdService.initCaptions(projectId);
        } catch {
          // シーン未生成などでcaptions/initできないだけ。タイミング取得自体は成功扱いにする
        }
      }
      applyData(next, false);
      showToast('歌詞タイミングを取得しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsFetchingTimings(false);
    }
  }, [projectId, applyData, showToast, handleActionError]);

  /**
   * 「この曲で進める」。歌詞タイミングが未取得なら自動取得してからストーリーボードへ進む（v4 F8）。
   * 取得に失敗してもブロックしない（③のシーン分割は按分フォールバックで進められる設計のため、
   * トーストで知らせるだけで遷移は続行する）。
   */
  const handleProceedToStoryboard = useCallback(async () => {
    if (projectId && !(project?.lyric_timings?.length)) {
      setIsFetchingTimings(true);
      try {
        const next = await songAdService.fetchTimings(projectId);
        applyData(next, false);
      } catch (e) {
        showToast(`歌詞タイミングの取得に失敗しました（${songAdService.extractErrorMessage(e)}）。あとで手動でも取得できます。`, 'error');
      } finally {
        setIsFetchingTimings(false);
      }
    }
    setActiveStep('storyboard');
  }, [projectId, project, applyData, showToast]);

  /**
   * ①情報登録タブの保存（v5 e。旧「条件を変更」モーダルの保存処理を移設）。
   * `InfoStep` がローカルドラフトから組み立てたパラメータをそのままPATCHする。
   * v6 F1（n）: 保存成功後は②歌詞へ自動遷移する（②歌詞→③楽曲の handleSaveFixedLyrics と同じ形）。
   * 失敗時は遷移しない。
   */
  const handleSaveInfo = useCallback(
    async (params: UpdateSongAdProjectParams): Promise<boolean> => {
      if (!projectId) return false;
      setError(null);
      try {
        const next = await songAdService.updateProject(projectId, params);
        applyData(next, true);
        showToast('情報を保存しました');
        setActiveStep('lyrics');
        return true;
      } catch (e) {
        handleActionError(e);
        return false;
      }
    },
    [projectId, applyData, showToast, handleActionError]
  );

  /**
   * 詳細編集タイムラインの自動保存。EditorStep内のdebounceタイマーから呼ばれる。
   * ここではエラーをtry/catchしない（EditorStep側が保存状態インジケータに反映するため呼び出し元へ投げる）。
   * 他ステップのローカルドラフト（歌詞・シーン設定）は無関係なので resetDrafts=false。
   */
  const handleSaveTimeline = useCallback(
    async (timeline: SongAdTimeline) => {
      if (!projectId) return;
      const next = await songAdService.updateTimeline(projectId, timeline);
      applyData(next, false);
    },
    [projectId, applyData]
  );

  /** ⑤プレビューのCTAトグル。cta_enabled のみPATCHし、ドラフトは触らない（他ステップの編集を壊さない） */
  const handleUpdateCtaEnabled = useCallback(
    async (enabled: boolean) => {
      if (!projectId) return;
      setError(null);
      try {
        const next = await songAdService.updateProject(projectId, { cta_enabled: enabled });
        applyData(next, false);
      } catch (e) {
        handleActionError(e);
      }
    },
    [projectId, applyData, handleActionError]
  );

  /**
   * ⑤プレビューのCTA文言。既存の required_words/banned_words/logo_image_key は保ったまま
   * cta のみ差し替える（v6 F2: logo_image_key を落とすとロゴ指定が意図せず解除されるため必須）。
   */
  const handleUpdateCtaText = useCallback(
    async (text: string) => {
      if (!projectId || !project) return;
      setError(null);
      try {
        const next = await songAdService.updateProject(projectId, {
          advanced_settings: {
            required_words: project.advanced_settings?.required_words ?? [],
            banned_words: project.advanced_settings?.banned_words ?? [],
            cta: text.trim() || null,
            logo_image_key: project.advanced_settings?.logo_image_key ?? null,
          },
        });
        applyData(next, false);
      } catch (e) {
        handleActionError(e);
      }
    },
    [projectId, project, applyData, handleActionError]
  );

  const handleCompose = useCallback(async () => {
    if (!projectId) return;
    setIsComposeRequesting(true);
    setError(null);
    try {
      await songAdService.compose(projectId);
      await fetchProject(false);
      showToast('合成を開始しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsComposeRequesting(false);
    }
  }, [projectId, fetchProject, showToast, handleActionError]);

  const scenes = useMemo(() => project?.scenes ?? [], [project]);

  // v8: ④ストーリーボードに未生成の絵コンテがある状態で表示されたら自動で一括生成を開始する
  // （「この曲で進める」→シーン分割→カード表示、のあと手動でボタンを押す一手間を解消）。
  // シーンがまだ無い場合（generate-scenes前）は何もしない。タブを行き来しても再実行しないよう、
  // プロジェクトごとに一度だけ発火するrefフラグを持つ（削除/AI修正で再びnoneに戻っても再度は自動実行しない
  // ＝ユーザーの意図しない連続課金を避ける）。
  const autoStoryboardGenTriggeredRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeStep !== 'storyboard' || !project) return;
    if (scenes.length === 0) return;
    if (autoStoryboardGenTriggeredRef.current === project.id) return;
    const hasPending = scenes.some((s) => s.storyboard.status === 'none' || s.storyboard.status === 'failed');
    if (!hasPending) return;
    autoStoryboardGenTriggeredRef.current = project.id;
    void handleGenerateAllStoryboards({ auto: true });
  }, [activeStep, project, scenes, handleGenerateAllStoryboards]);

  /** 後工程生成済みか（§8.5の確認モーダル要否）。曲かシーンが既に存在する場合 */
  const hasDownstream = useMemo(() => !!project?.song_url || scenes.length > 0, [project, scenes]);

  const steps = useMemo(() => {
    const sceneCount = scenes.length;
    const storyboardDone = scenes.filter((s) => s.storyboard.image).length;
    const motionTargets = sceneDrafts.filter((d) => d.motionMode === 'clip').length;
    const clipDone = scenes.filter((s) => {
      const draft = sceneDrafts.find((d) => d.id === s.id);
      return (draft?.motionMode ?? s.motion_mode) === 'clip' && s.clip.video;
    }).length;
    const finalDone = !!project?.final_video_url;
    const canPreview = !!project?.song_url && sceneCount > 0 && storyboardDone === sceneCount;

    return [
      {
        key: 'info' as StepKey,
        label: '① 情報登録',
        icon: ClipboardList,
        badge: null,
        // 商品またはタイトルが確定していれば済み扱い（v5 e。タイトルは自動命名済みなので通常true）
        done: !!project?.product_id || !!project?.title,
      },
      {
        key: 'lyrics' as StepKey,
        label: '② 歌詞',
        icon: FileText,
        badge: null,
        done: !!project?.lyrics_fixed_at,
      },
      {
        key: 'song' as StepKey,
        label: '③ 楽曲',
        icon: Music2,
        badge: null,
        done: !!project?.song_url,
      },
      {
        key: 'storyboard' as StepKey,
        label: '④ ストーリーボード',
        icon: ImageIcon,
        badge: sceneCount > 0 ? `${storyboardDone}/${sceneCount}` : null,
        done: sceneCount > 0 && storyboardDone === sceneCount,
      },
      {
        key: 'motion' as StepKey,
        label: '⑤ モーション',
        icon: Wand2,
        badge: motionTargets > 0 ? `${clipDone}/${motionTargets}` : null,
        done: motionTargets > 0 ? clipDone === motionTargets : sceneCount > 0 && storyboardDone === sceneCount,
      },
      {
        key: 'preview' as StepKey,
        label: '⑥ プレビュー',
        icon: PlayCircle,
        badge: null,
        done: canPreview,
      },
      {
        key: 'export' as StepKey,
        label: '⑦ 書き出し',
        icon: Combine,
        badge: null,
        done: finalDone,
      },
    ];
  }, [project, scenes, sceneDrafts]);

  if (isLoading) {
    return (
      <ResponsiveAppShell
        sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
        mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
        topbarTitle="歌広告"
      >
        <div className="flex min-h-[calc(100dvh-56px)] items-center justify-center bg-warm-bg text-gray-400 lg:h-screen">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          読み込み中...
        </div>
      </ResponsiveAppShell>
    );
  }

  if (!project) {
    return (
      <ResponsiveAppShell
        sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
        mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
        topbarTitle="歌広告"
      >
        <div className="flex min-h-[calc(100dvh-56px)] flex-col items-center justify-center bg-warm-bg px-4 text-center text-gray-500 lg:h-screen">
          <p className="text-sm mb-3">{error ?? 'プロジェクトが見つかりません'}</p>
          <button
            onClick={() => navigate('/ads/song')}
            className="px-4 py-2 text-sm bg-white border border-warm-border rounded-tasky-sm hover:bg-gray-100"
          >
            一覧へ戻る
          </button>
        </div>
      </ResponsiveAppShell>
    );
  }

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle={project.title || '歌広告'}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-warm-bg lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title={project.title}
          icon={<Music2 className="w-5 h-5" />}
          leading={(
            <button
              onClick={() => navigate('/ads/song')}
              className="p-1 h-8 w-8 hover:bg-gray-100 rounded-md shrink-0"
              title="一覧へ戻る"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* ステップタブ */}
        <div data-tutorial="ads-song-steps" className="shrink-0 overflow-x-auto border-b border-warm-border bg-white px-4">
          <div className="mx-auto flex w-max max-w-6xl gap-1 lg:w-auto">
            {steps.map((step) => (
              <button
                key={step.key}
                onClick={() => setActiveStep(step.key)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm border-b-2 transition-colors ${
                  activeStep === step.key
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <step.icon className="w-4 h-4" />
                {step.label}
                {step.badge && (
                  <TaskyBadge variant={step.done ? 'success' : 'secondary'} className="text-[11px]">
                    {step.badge}
                  </TaskyBadge>
                )}
                {step.done && !step.badge && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
              </button>
            ))}
          </div>
        </div>

        {/* Main */}
        <div data-tutorial="ads-song-content" className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            {error && (
              <TaskyAlert tone="error" className="mb-4">
                {error}
              </TaskyAlert>
            )}

            {activeStep === 'info' && (
              <InfoStep project={project} onSave={handleSaveInfo} />
            )}

            {activeStep === 'lyrics' && (
              <LyricsStep
                project={project}
                lyricsDraft={lyricsDraft}
                kanaDraft={kanaDraft}
                onLyricsDraftChange={setLyricsDraft}
                onKanaDraftChange={setKanaDraft}
                isGenerating={isGeneratingLyrics}
                isSaving={isSavingLyrics}
                isRegeneratingKana={isRegeneratingKana}
                onGenerate={handleGenerateLyrics}
                onSaveFixed={handleSaveFixedLyrics}
                onRegenerateKana={handleRegenerateKana}
                hasDownstream={hasDownstream}
                onEditSettings={() => setActiveStep('info')}
              />
            )}

            {activeStep === 'song' && (
              <SongStep
                project={project}
                isGenerating={isGeneratingSong}
                onGenerate={handleGenerateSong}
                onProceed={handleProceedToStoryboard}
                onEditSettings={() => setActiveStep('info')}
                onFetchTimings={handleFetchTimings}
                isFetchingTimings={isFetchingTimings}
              />
            )}

            {activeStep === 'storyboard' && (
              <StoryboardStep
                scenes={scenes}
                sceneDrafts={sceneDrafts}
                aspectRatio={project.aspect_ratio}
                generatingSceneIds={generatingSceneIds}
                canGenerateScenes={!!project.lyrics_fixed_at}
                isGeneratingScenes={isGeneratingScenes}
                isSavingDrafts={isSavingSceneDrafts}
                isReordering={isReordering}
                onGenerateScenes={handleGenerateScenes}
                onGenerate={handleGenerateStoryboard}
                onGenerateAll={handleGenerateAllStoryboards}
                isGeneratingAll={isGeneratingAllStoryboards}
                generationCoolingDown={storyboardCoolingDown}
                onSceneLyricLinesChange={handleSceneLyricLinesChange}
                onSaveDrafts={handleSaveSceneDrafts}
                onReorder={handleReorderScenes}
                onAddScene={handleAddScene}
                onDeleteScene={handleDeleteScene}
                onAiEditScene={handleAiEditScene}
                onReplaceSceneImage={handleReplaceSceneImage}
                onProceedToMotion={() => setActiveStep('motion')}
              />
            )}

            {activeStep === 'motion' && (
              <MotionStep
                scenes={scenes}
                sceneDrafts={sceneDrafts}
                aspectRatio={project.aspect_ratio}
                generatingSceneIds={generatingSceneIds}
                isSavingDrafts={isSavingSceneDrafts}
                isMotionAutoRequesting={isMotionAutoRequesting}
                onSetMotionMode={handleSetMotionMode}
                onSetMotionModeAll={handleSetMotionModeAll}
                onMotionPromptChange={handleMotionPromptChange}
                onTelopPositionChange={handleTelopPositionChange}
                onTelopSizeChange={handleTelopSizeChange}
                onSaveAndProceed={handleSaveMotionAndProceed}
                onMotionAuto={handleMotionAuto}
                onGenerate={handleGenerateClip}
                onGenerateAll={handleGenerateAllClips}
              />
            )}

            {activeStep === 'preview' && (
              <PreviewStep
                project={project}
                scenes={scenes}
                onProceedToExport={() => setActiveStep('export')}
                onProceedToEditor={() => setActiveStep('editor')}
                onUpdateCtaEnabled={handleUpdateCtaEnabled}
                onUpdateCtaText={handleUpdateCtaText}
                onEditSettings={() => setActiveStep('info')}
              />
            )}

            {activeStep === 'editor' && (
              <EditorStep
                project={project}
                scenes={scenes}
                onSaveTimeline={handleSaveTimeline}
                onBackToPreview={() => setActiveStep('preview')}
                onProceedToExport={() => setActiveStep('export')}
              />
            )}

            {activeStep === 'export' && (
              <ExportStep
                project={project}
                scenes={scenes}
                isRequesting={isComposeRequesting}
                onCompose={handleCompose}
                onGoToLyrics={() => setActiveStep('lyrics')}
                onGoToSong={() => setActiveStep('song')}
                onGoToStoryboard={() => setActiveStep('storyboard')}
                onGoToMotion={() => setActiveStep('motion')}
              />
            )}
          </TaskyPageContainer>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default SongAdEditorPage;
